class RequiredInput extends HTMLElement {
  constructor () {
    super()

    this.validationTypes = ['valueMissing', 'typeMismatch', 'patternMismatch', 'customError', 'badInput']
  }

  connectedCallback () {
    this.enforcePasswordStrength = this.getAttribute('enforce-password-strength') === 'true'
    this.generalPasswordError = this.getAttribute('data-general-error')
    this.inputToRequire = this.querySelector('input') || false

    if (this.hasAttribute('data-valid-color')) {
      this.validClasses = this.getAttribute('data-valid-color')
    }

    if (this.hasAttribute('data-invalid-color')) {
      this.invalidClasses = this.getAttribute('data-invalid-color')
    }

    if (this.enforcePasswordStrength) {
      this.setupPasswordEnforcement()
    } else {
      this.setupRequiredField()
    }
  }

  setupRequiredField () {
    this.inputErrorMessage = this.querySelector('[data-validation-message]') || false
    // allows for initial error
    this.messageIcon = this.inputErrorMessage.innerHTML

    if (!this.inputErrorMessage) {
      console.error(`Error message container missing for #${this.inputToRequire.id}`)
    } else {
      this.inputToRequire.addEventListener('invalid', (event) => {
        const inputValidity = this.inputToRequire.validity
        const validationFailType = this.validationTypes.find((validationType) => {
          return inputValidity[validationType] && this.inputErrorMessage.hasAttribute(`data-validation-${validationType}`)
        })

        if (validationFailType) {
          this.inputErrorMessage.innerHTML = this.messageIcon + this.inputErrorMessage.getAttribute(`data-validation-${validationFailType}`)
        } else {
          this.inputErrorMessage.innerHTML = this.messageIcon + this.inputToRequire.validationMessage
        }
        this.inputErrorMessage.classList.remove('visible')
        this.inputErrorMessage.classList.add('hidden')
        this.inputToRequire.classList.add('c-border-error-on')
      })
      this.inputToRequire.addEventListener('change', () => {
        this.inputErrorMessage.classList.add('hidden')
        this.inputErrorMessage.classList.remove('visible')
        this.inputToRequire.classList.remove('c-border-error-on')
      })
    }
  }

  setupPasswordEnforcement () {
    this.passwordLengthMessage = this.querySelector('[data-password-message-length]')
    this.passwordNumericMessage = this.querySelector('[data-password-message-numerals]')
    this.passwordCasingMessage = this.querySelector('[data-password-message-casing]')

    this.passwordConfirmMessage = this.querySelector('[data-password-message-confirm]')
    if (this.passwordConfirmMessage !== undefined && this.passwordConfirmMessage !== null) {
      const confirmedInputID = this.getAttribute('data-password-confirmed-id')
      if (confirmedInputID) {
        this.passwordConfirmedInput = document.getElementById(confirmedInputID)
        this.passwordConfirmedInput.addEventListener('input', () => {
          this.inputToRequire.dispatchEvent(new Event('input'))
        })
        this.passwordConfirmedInput.addEventListener('invalid', () => {
          this.passwordConfirmedInput.classList.add('c-border-error-on')
        })
      }
    }

    this.inputToRequire.setCustomValidity(this.generalPasswordError)
    this.inputToRequire.addEventListener('input', () => this.checkPasswordStrength())
    this.inputToRequire.addEventListener('invalid', () => {
      this.inputToRequire.classList.add('c-border-error-on')
    })
  }

  checkPasswordStrength () {

    const newPassword = this.inputToRequire?.value.trim();
    const requirements = {
      isLongEnough: newPassword.length >= 8,
      hasNumeric: /\d/.test(newPassword),
      hasUppercase: /[A-Z]/.test(newPassword),
      hasLowercase: /[a-z]/.test(newPassword),
    }

    const meetsRequirements = Object.values(requirements).every((requirement) => requirement)
    this.inputToRequire.setCustomValidity(meetsRequirements ? '' : this.generalPasswordError)
    this.inputToRequire.setAttribute('valid', meetsRequirements)
    this.inputToRequire.classList.remove('c-border-error-on')

    if (this.passwordLengthMessage) this.updateRequirementMessageClasses(this.passwordLengthMessage, requirements.isLongEnough)
    if (this.passwordNumericMessage) this.updateRequirementMessageClasses(this.passwordNumericMessage, requirements.hasNumeric)
    if (this.passwordCasingMessage) this.updateRequirementMessageClasses(this.passwordCasingMessage, requirements.hasUppercase && requirements.hasLowercase)

    if (this.passwordConfirmedInput !== undefined && this.passwordConfirmedInput !== null) {
      const checkPasswordConfirmed = this.passwordConfirmedInput.value === newPassword
      this.passwordConfirmedInput.setCustomValidity(checkPasswordConfirmed ? '' : this.generalPasswordError)
      this.passwordConfirmedInput.setAttribute('valid', `${checkPasswordConfirmed}`)
      this.passwordConfirmedInput.classList.remove('c-border-error-on')
      this.updateRequirementMessageClasses(this.passwordConfirmMessage, checkPasswordConfirmed)
    }
  }

  updateRequirementMessageClasses (messageElement, meetsRequirements) {
    const isValidIcon = messageElement.querySelector('[data-message-valid]')
    const isInvalidIcon = messageElement.querySelector('[data-message-invalid]')
    const iconToShow = meetsRequirements ? isValidIcon : isInvalidIcon
    const iconToHide = !meetsRequirements ? isValidIcon : isInvalidIcon
    iconToHide.classList.add('hidden')
    iconToHide.classList.remove('visible')
    iconToShow.classList.remove('hidden')
    iconToShow.classList.add('visible')

    const toRemove = meetsRequirements ? this.invalidClasses : this.validClasses
    const toAdd = !meetsRequirements ? this.invalidClasses : this.validClasses

    // sometimes strings are empty
    if (toRemove) {
      messageElement.classList.remove(toRemove)
    }
    if (toAdd) {
      messageElement.classList.add(toAdd)
    }
  }
}

if (!customElements.get('required-input')) {
  customElements.define('required-input', RequiredInput)
}
